// Every write to a project document goes through these functions (browser, server and agent tools).
// Each runs in one transaction with the given origin, so undo and activity can tell who did what.
//
// Unknown IDs (settled in M2; the MCP tools rely on this):
// - The creators (addCard, addColumn, addArrow, ensureColumn, addRoute, addDrawnRoute) throw OpsError
//   when their board is unknown, their input is invalid (a place's coordinates; a route id that
//   already exists), or (addCard) the kind doesn't belong where it was sent — and check before
//   writing anything, so a refused call leaves the document untouched.
// - Every other function is an edit that can race with someone else's delete on another replica.
//   It never throws: when its target (board, card, column, item, route) is unknown or malformed, it
//   does nothing.
// - Server code therefore resolves and checks every ID itself before calling an op and reports
//   unknown ones as 404s; it never relies on an op's silent no-op.
import * as Y from 'yjs';
import { randomId } from './ids.js';
import {
  DEFAULT_SIZE, FIXED_COLUMNS, LIMITS, SHARED_BOARDS, boardMap, boardsMap, cardsMap, parsePlacement, posSchema, routesMap, sparkArray,
  type Actor, type BoardKind, type CardKind, type Pos, type RouteSource, type Size, type Source, type TrayState, type WebImage,
} from './doc.js';
import { drawnRouteFields, validLonLat, type LonLat } from './geo.js';

/**
 * A refused write: an unknown board for a creator, a card kind that doesn't belong where it was
 * sent, or (invalid_input) a value the shared schema would reject anyway — refused up front so a
 * bad write never lands in the document invisible and undeletable (e.g. a place with lat 91, or a
 * route id that already exists).
 */
export class OpsError extends Error {
  constructor(
    readonly code: 'unknown_board' | 'wrong_kind' | 'invalid_input',
    message: string,
  ) {
    super(message);
    this.name = 'OpsError';
  }
}

/** Origin for edits made by this client; the browser's UndoManager tracks only this origin. */
export const LOCAL = Symbol('plana-local');

/**
 * Origin for this client's own writes that must stay off its own undo stack: the touch-up
 * stamp when a note loses focus (no visible change to undo), and an async link-preview result
 * (⌘Z should remove the card the user pasted, not revert it to "Fetching preview…"). Untracked
 * by the UndoManager, so ⌘Z/⇧⌘Z skip straight past these to the user's actual last change.
 */
export const SILENT = Symbol('plana-silent');

const nowIso = () => new Date().toISOString();

export type NewCard =
  | { kind: 'note'; text?: string }
  | { kind: 'image'; assetId?: string; url?: string; sourcePageUrl?: string; thumbAssetId?: string; credit?: string; caption?: string; width?: number; height?: number }
  | { kind: 'link'; url: string; title?: string; description?: string; siteName?: string; imageAssetId?: string | null; previewStatus?: 'pending' | 'ok' | 'failed' }
  | { kind: 'palette'; colours: { hex: string; name?: string }[] }
  | { kind: 'todo'; title?: string; items?: string[] }
  | { kind: 'finding'; claim: string; sources: Source[]; status: 'researched' | 'question'; images?: WebImage[] }
  | { kind: 'place'; name: string; lat: number; lon: number; address?: string; notes?: string };

/**
 * Where a new card goes: a board position (optionally a column), the Spark inbox, (findings only)
 * the Suggested tray, or (places only) just the Map. Every place card is a pin on the Map wherever
 * else it sits.
 */
export type Where = { boardId: string; pos: Pos; columnId?: string } | { spark: true } | { tray: true } | { map: true };

function newBoard(kind: BoardKind, title: string, pitchId?: string) {
  const b = new Y.Map<any>();
  b.set('kind', kind);
  b.set('title', title);
  if (pitchId) b.set('pitchId', pitchId);
  b.set('placements', new Y.Map());
  b.set('columns', new Y.Map());
  b.set('arrows', new Y.Map());
  return b;
}

export function initProjectDoc(doc: Y.Doc, origin: unknown = null) {
  doc.transact(() => {
    const boards = boardsMap(doc);
    if (!boards.has(SHARED_BOARDS.research)) boards.set(SHARED_BOARDS.research, newBoard('research', 'Research'));
    if (!boards.has(SHARED_BOARDS.moodboard)) boards.set(SHARED_BOARDS.moodboard, newBoard('moodboard', 'Moodboard'));
  }, origin);
}

/** Replaces a note's content with plain text, one paragraph per line. */
export function setNoteText(fragment: Y.XmlFragment, text: string) {
  fragment.delete(0, fragment.length);
  const lines = text.split(/\r?\n/);
  fragment.insert(0, lines.map(() => new Y.XmlElement('paragraph')));
  lines.forEach((line, i) => {
    const p = fragment.get(i) as Y.XmlElement;
    if (line) {
      p.insert(0, [new Y.XmlText()]);
      (p.get(0) as Y.XmlText).insert(0, line);
    }
  });
}

function stamp(card: Y.Map<any>, actor: Actor) {
  card.set('updatedBy', actor);
  card.set('updatedAt', nowIso());
}

/** Highest `z` among a board's placements. Malformed data (not a Y.Map, or an entry that isn't
 * one) is skipped rather than crashing a write that touches every card's stacking order. */
function maxZ(placements: unknown): number {
  let z = 0;
  if (placements instanceof Y.Map) placements.forEach((p: unknown) => p instanceof Y.Map && (z = Math.max(z, p.get('z') ?? 0)));
  return z;
}

function setPlacement(board: Y.Map<any>, cardId: string, kind: CardKind | undefined, pos: Pos, columnId: string | null) {
  const placements = board.get('placements') as Y.Map<any>;
  let p = placements.get(cardId) as Y.Map<any> | undefined;
  if (!p) {
    p = placements.set(cardId, new Y.Map<any>());
    p.set('size', DEFAULT_SIZE[kind ?? 'note']);
  }
  p.set('pos', { x: Math.round(pos.x), y: Math.round(pos.y) });
  p.set('z', maxZ(placements) + 1);
  p.set('columnId', columnId);
  return p;
}

function removeFromSpark(doc: Y.Doc, cardId: string) {
  const spark = sparkArray(doc);
  for (let i = spark.length - 1; i >= 0; i--) if (spark.get(i) === cardId) spark.delete(i, 1);
}

export function addCard(doc: Y.Doc, actor: Actor, input: NewCard, where: Where, origin: unknown = LOCAL): string {
  const id = randomId('cd');
  doc.transact(() => {
    // Check the destination before writing anything (see the policy at the top of this file).
    const board = 'boardId' in where ? boardMap(doc, where.boardId) : undefined;
    if ('boardId' in where && !board) throw new OpsError('unknown_board', `Unknown board ${where.boardId}`);
    if (input.kind === 'finding' && !('tray' in where)) throw new OpsError('wrong_kind', 'Findings start in the Suggested tray');
    if ('tray' in where && input.kind !== 'finding') throw new OpsError('wrong_kind', 'Only findings go to the Suggested tray');
    if ('map' in where && input.kind !== 'place') throw new OpsError('wrong_kind', 'Only places go straight onto the map');
    // A place whose coordinates the shared schema would reject (e.g. lat 91, or a non-finite value)
    // is refused here rather than written: cardSchema would then parse it as null forever after,
    // leaving an invisible card nothing can select, edit or delete.
    if (input.kind === 'place' && !validLonLat([input.lon, input.lat])) {
      throw new OpsError('invalid_input', `Invalid place coordinates: lat ${input.lat}, lon ${input.lon}`);
    }
    const card = cardsMap(doc).set(id, new Y.Map<any>());
    const at = nowIso();
    card.set('id', id);
    card.set('kind', input.kind);
    card.set('createdBy', actor);
    card.set('createdAt', at);
    card.set('updatedBy', actor);
    card.set('updatedAt', at);
    card.set('tags', []);
    card.set('placeCardId', null);
    card.set('comments', new Y.Array());
    card.set('reactions', new Y.Map());
    switch (input.kind) {
      case 'note': {
        const body = card.set('body', new Y.XmlFragment());
        setNoteText(body, input.text ?? '');
        break;
      }
      case 'image':
        for (const k of ['assetId', 'url', 'sourcePageUrl', 'thumbAssetId'] as const) card.set(k, input[k] ?? null);
        card.set('credit', input.credit ?? '');
        card.set('caption', input.caption ?? '');
        card.set('width', input.width ?? 0);
        card.set('height', input.height ?? 0);
        break;
      case 'link':
        card.set('url', input.url);
        card.set('title', input.title ?? '');
        card.set('description', input.description ?? '');
        card.set('siteName', input.siteName ?? '');
        card.set('imageAssetId', input.imageAssetId ?? null);
        card.set('previewStatus', input.previewStatus ?? 'pending');
        break;
      case 'palette':
        card.set('colours', input.colours.map((c) => ({ hex: c.hex.toLowerCase(), name: c.name ?? '' })));
        break;
      case 'todo': {
        card.set('title', input.title ?? '');
        const items = card.set('items', new Y.Array<Y.Map<any>>());
        for (const text of input.items ?? []) items.push([todoItem(text)]);
        break;
      }
      case 'finding':
        card.set('claim', input.claim.slice(0, LIMITS.claim));
        card.set('sources', input.sources.map((s) => ({ url: s.url, title: s.title, accessedAt: s.accessedAt })));
        card.set('status', input.status);
        card.set('trayState', 'suggested');
        card.set('images', (input.images ?? []).map((i) => ({ url: i.url, sourcePageUrl: i.sourcePageUrl, credit: i.credit, thumbAssetId: i.thumbAssetId })));
        card.set('trayBy', null);
        card.set('trayAt', null);
        break;
      case 'place':
        card.set('name', input.name.slice(0, LIMITS.title));
        card.set('lat', input.lat);
        card.set('lon', input.lon);
        card.set('address', (input.address ?? '').slice(0, LIMITS.address));
        card.set('notes', (input.notes ?? '').slice(0, LIMITS.placeNotes));
        break;
    }
    if (board && 'boardId' in where) {
      setPlacement(board, id, input.kind, where.pos, where.columnId ?? null);
      if (where.columnId) insertInColumn(board, id, where.columnId, Infinity);
    } else if ('spark' in where) sparkArray(doc).insert(0, [id]);
    // { tray: true }: no placement. The tray lists every finding whose effective state is "suggested".
    // { map: true }: no placement either. The Map shows every place card as a pin.
  }, origin);
  return id;
}

function todoItem(text: string) {
  const m = new Y.Map<any>();
  m.set('id', randomId('ti'));
  m.set('text', text);
  m.set('done', false);
  return m;
}

/** Scalar fields only (title, url, caption, colours, tags…); note bodies are edited through Tiptap. */
export function updateCard(doc: Y.Doc, actor: Actor, cardId: string, fields: Record<string, unknown>, origin: unknown = LOCAL) {
  const forbidden = new Set(['id', 'kind', 'createdBy', 'createdAt', 'comments', 'reactions', 'body', 'items']);
  doc.transact(() => {
    const card = cardsMap(doc).get(cardId);
    if (!card) return;
    for (const [k, v] of Object.entries(fields)) if (!forbidden.has(k)) card.set(k, v);
    stamp(card, actor);
  }, origin);
}

export function cardBoards(doc: Y.Doc, cardId: string): string[] {
  const out: string[] = [];
  boardsMap(doc).forEach((b, boardId) => {
    // A board whose placements field isn't a Y.Map at all (malformed data) has nothing to find
    // the card in, rather than crashing every caller (trayFindings, get_board, removeOrDeleteCard…).
    const placements = b.get('placements');
    if (placements instanceof Y.Map && placements.has(cardId)) out.push(boardId);
  });
  return out;
}

/** Deletes a card everywhere. A small tombstone (`deleted:<cardId>` in meta) lets open panels say who deleted it. */
export function deleteCard(doc: Y.Doc, actor: Actor, cardId: string, origin: unknown = LOCAL) {
  doc.transact(() => {
    const meta = doc.getMap<unknown>('meta');
    if (cardsMap(doc).has(cardId)) {
      meta.set(`deleted:${cardId}`, { by: actor.name, at: nowIso() });
      const tombstones = [...meta.keys()].filter((k) => k.startsWith('deleted:'));
      if (tombstones.length > 200) {
        tombstones
          .sort((a, b) => String((meta.get(a) as { at: string }).at).localeCompare(String((meta.get(b) as { at: string }).at)))
          .slice(0, tombstones.length - 200)
          .forEach((k) => meta.delete(k));
      }
    }
    boardsMap(doc).forEach((b) => {
      (b.get('placements') as Y.Map<any>).delete(cardId);
      (b.get('columns') as Y.Map<Y.Map<any>>).forEach((col) => removeFromColumnCardIds(col, cardId));
      const arrows = b.get('arrows') as Y.Map<any>;
      for (const [aid, a] of [...arrows.entries()]) if (a.from === cardId || a.to === cardId) arrows.delete(aid);
    });
    removeFromSpark(doc, cardId);
    cardsMap(doc).delete(cardId);
  }, origin);
}

function removeId(arr: Y.Array<string>, id: string) {
  for (let i = arr.length - 1; i >= 0; i--) if (arr.get(i) === id) arr.delete(i, 1);
}

/**
 * Removes `cardId` from a column's `cardIds`, tolerating malformed column data (a future version,
 * or a buggy agent): a column value that isn't a Y.Map, or one with no `cardIds` Y.Array, is
 * skipped rather than crashing the write.
 */
function removeFromColumnCardIds(col: unknown, cardId: string) {
  if (!(col instanceof Y.Map)) return;
  const ids = col.get('cardIds');
  if (ids instanceof Y.Array) removeId(ids, cardId);
}

export function placeCard(doc: Y.Doc, boardId: string, cardId: string, pos: Pos, origin: unknown = LOCAL) {
  doc.transact(() => {
    const board = boardMap(doc, boardId);
    const card = cardsMap(doc).get(cardId);
    if (!board || !card) return;
    setPlacement(board, cardId, card.get('kind'), pos, null);
    removeFromSpark(doc, cardId);
  }, origin);
}

export function removeFromBoard(doc: Y.Doc, boardId: string, cardId: string, origin: unknown = LOCAL) {
  doc.transact(() => {
    const board = boardMap(doc, boardId);
    if (!board) return;
    (board.get('placements') as Y.Map<any>).delete(cardId);
    (board.get('columns') as Y.Map<Y.Map<any>>).forEach((col) => removeFromColumnCardIds(col, cardId));
  }, origin);
}

/** Whole-position writes keep x and y together, so concurrent drags never mix coordinates. */
export function moveCards(doc: Y.Doc, boardId: string, moves: { cardId: string; pos: Pos }[], origin: unknown = LOCAL) {
  doc.transact(() => {
    const placements = boardMap(doc, boardId)?.get('placements') as Y.Map<Y.Map<any>> | undefined;
    if (!placements) return;
    for (const m of moves) placements.get(m.cardId)?.set('pos', { x: Math.round(m.pos.x), y: Math.round(m.pos.y) });
  }, origin);
}

export function resizeCard(doc: Y.Doc, boardId: string, cardId: string, size: Size, origin: unknown = LOCAL) {
  doc.transact(() => {
    const w = Math.min(1600, Math.max(120, Math.round(size.w)));
    const h = size.h === null ? null : Math.min(2000, Math.max(60, Math.round(size.h)));
    (boardMap(doc, boardId)?.get('placements') as Y.Map<Y.Map<any>> | undefined)?.get(cardId)?.set('size', { w, h });
  }, origin);
}

export function bringToFront(doc: Y.Doc, boardId: string, cardIds: string[], origin: unknown = LOCAL) {
  doc.transact(() => {
    const placements = boardMap(doc, boardId)?.get('placements') as Y.Map<Y.Map<any>> | undefined;
    if (!placements) return;
    let z = maxZ(placements);
    for (const id of cardIds) placements.get(id)?.set('z', ++z);
  }, origin);
}

export function addColumn(doc: Y.Doc, boardId: string, title: string, pos: Pos, origin: unknown = LOCAL): string {
  const id = randomId('cl');
  doc.transact(() => {
    const board = boardMap(doc, boardId);
    if (!board) throw new OpsError('unknown_board', `Unknown board ${boardId}`);
    const cols = board.get('columns') as Y.Map<any>;
    const col = cols.set(id, new Y.Map<any>());
    col.set('title', title.slice(0, 200));
    col.set('pos', { x: Math.round(pos.x), y: Math.round(pos.y) });
    col.set('w', 280);
    col.set('cardIds', new Y.Array<string>());
  }, origin);
  return id;
}

/** A column's Y.Map, or undefined when the board or column is unknown or malformed (not a Y.Map). */
function columnMap(doc: Y.Doc, boardId: string, columnId: string): Y.Map<any> | undefined {
  const cols = boardMap(doc, boardId)?.get('columns');
  const col = cols instanceof Y.Map ? cols.get(columnId) : undefined;
  return col instanceof Y.Map ? col : undefined;
}

export function renameColumn(doc: Y.Doc, boardId: string, columnId: string, title: string, origin: unknown = LOCAL) {
  doc.transact(() => {
    columnMap(doc, boardId, columnId)?.set('title', title.slice(0, 200));
  }, origin);
}

export function moveColumn(doc: Y.Doc, boardId: string, columnId: string, pos: Pos, origin: unknown = LOCAL) {
  doc.transact(() => {
    columnMap(doc, boardId, columnId)?.set('pos', { x: Math.round(pos.x), y: Math.round(pos.y) });
  }, origin);
}

/**
 * Deleting a column keeps its cards: they are laid out on the canvas where the column was. A column
 * with no valid position frees its cards at the origin; a value that isn't a column at all is removed.
 */
export function deleteColumn(doc: Y.Doc, boardId: string, columnId: string, origin: unknown = LOCAL) {
  doc.transact(() => {
    const board = boardMap(doc, boardId);
    const cols = board?.get('columns');
    if (!board || !(cols instanceof Y.Map) || !cols.has(columnId)) return;
    const col = cols.get(columnId);
    const parsed = col instanceof Y.Map ? posSchema.safeParse(col.get('pos')) : null;
    const { x, y } = parsed?.success ? parsed.data : { x: 0, y: 0 };
    const placements = board.get('placements') as Y.Map<Y.Map<any>>;
    columnCards(doc, boardId, columnId).forEach((cardId, i) => {
      const p = placements.get(cardId);
      p?.set('columnId', null);
      p?.set('pos', { x, y: y + 48 + i * 160 });
    });
    cols.delete(columnId);
  }, origin);
}

/** Returns true if the card actually landed in the column's `cardIds` (false for malformed column data). */
function insertInColumn(board: Y.Map<any>, cardId: string, columnId: string, index: number): boolean {
  const cols = board.get('columns') as Y.Map<Y.Map<any>>;
  cols.forEach((c) => removeFromColumnCardIds(c, cardId));
  const col = cols.get(columnId);
  if (!(col instanceof Y.Map)) return false;
  const ids = col.get('cardIds');
  if (!(ids instanceof Y.Array)) return false;
  ids.insert(Math.max(0, Math.min(index, ids.length)), [cardId]);
  return true;
}

export function putInColumn(doc: Y.Doc, boardId: string, cardId: string, columnId: string, index: number, origin: unknown = LOCAL) {
  doc.transact(() => {
    const board = boardMap(doc, boardId);
    const p = (board?.get('placements') as Y.Map<Y.Map<any>> | undefined)?.get(cardId);
    if (!board || !p) return;
    // A malformed column (future version, buggy agent) is skipped: the card keeps its old
    // placement rather than pointing at a column it was never actually inserted into.
    if (insertInColumn(board, cardId, columnId, index)) p.set('columnId', columnId);
  }, origin);
}

export function takeOutOfColumn(doc: Y.Doc, boardId: string, cardId: string, pos: Pos, origin: unknown = LOCAL) {
  doc.transact(() => {
    const board = boardMap(doc, boardId);
    if (!board) return;
    (board.get('columns') as Y.Map<Y.Map<any>>).forEach((c) => removeFromColumnCardIds(c, cardId));
    const p = (board.get('placements') as Y.Map<Y.Map<any>>).get(cardId);
    p?.set('columnId', null);
    p?.set('pos', { x: Math.round(pos.x), y: Math.round(pos.y) });
  }, origin);
}

/** Cards shown in a column: listed in its order AND whose placement points at it (the placement wins). */
export function columnCards(doc: Y.Doc, boardId: string, columnId: string): string[] {
  const board = boardMap(doc, boardId);
  const col = (board?.get('columns') as Y.Map<Y.Map<any>> | undefined)?.get(columnId);
  // A malformed column (future version, buggy agent) — not a Y.Map at all, or without a
  // `cardIds` Y.Array — has nothing to list rather than crashing.
  if (!board || !(col instanceof Y.Map)) return [];
  const cardIds = col.get('cardIds');
  if (!(cardIds instanceof Y.Array)) return [];
  // Same tolerance for the board's placements field itself: malformed, it just matches nothing.
  const placements = board.get('placements');
  if (!(placements instanceof Y.Map)) return [];
  const seen = new Set<string>();
  return cardIds.toArray().filter((id) => {
    const p = placements.get(id);
    const placedColumnId = p instanceof Y.Map ? p.get('columnId') : undefined;
    if (seen.has(id) || placedColumnId !== columnId) return false;
    seen.add(id);
    return true;
  });
}

export function addArrow(doc: Y.Doc, boardId: string, from: string, to: string, origin: unknown = LOCAL): string {
  const id = randomId('ar');
  if (from === to) return id;
  doc.transact(() => {
    const board = boardMap(doc, boardId);
    if (!board) throw new OpsError('unknown_board', `Unknown board ${boardId}`);
    (board.get('arrows') as Y.Map<any>).set(id, { from, to });
  }, origin);
  return id;
}

export function deleteArrow(doc: Y.Doc, boardId: string, arrowId: string, origin: unknown = LOCAL) {
  doc.transact(() => {
    const board = boardMap(doc, boardId);
    if (!board) return;
    (board.get('arrows') as Y.Map<any>).delete(arrowId);
  }, origin);
}

export function addComment(doc: Y.Doc, actor: Actor, cardId: string, text: string, origin: unknown = LOCAL): string {
  const id = randomId('cm');
  const clean = text.trim().slice(0, LIMITS.comment);
  if (!clean) return id;
  doc.transact(() => {
    (cardsMap(doc).get(cardId)?.get('comments') as Y.Array<any> | undefined)?.push([{ id, author: actor, text: clean, at: nowIso() }]);
  }, origin);
  return id;
}

export function deleteComment(doc: Y.Doc, cardId: string, commentId: string, origin: unknown = LOCAL) {
  doc.transact(() => {
    const arr = cardsMap(doc).get(cardId)?.get('comments') as Y.Array<any> | undefined;
    if (!arr) return;
    for (let i = arr.length - 1; i >= 0; i--) if (arr.get(i).id === commentId) arr.delete(i, 1);
  }, origin);
}

/** Who a reaction belongs to: a person's user ID, or an agent's token ID (so an agent's 👍 never toggles its account owner's). */
export const reactorId = (actor: Actor) => (actor.kind === 'agent' && actor.tokenId ? actor.tokenId : actor.userId);

export function toggleReaction(doc: Y.Doc, actor: Actor, cardId: string, emoji: string, origin: unknown = LOCAL) {
  doc.transact(() => {
    const reactions = cardsMap(doc).get(cardId)?.get('reactions') as Y.Map<true> | undefined;
    if (!reactions || emoji.includes('|') || emoji.length > 16) return;
    // Deviation from spec §4 (which keys reactions by userId): an agent's half of the key is its
    // tokenId via reactorId, not its account's userId, so an agent's reaction never toggles the
    // reaction of the human who owns that account.
    const key = `${emoji}|${reactorId(actor)}`;
    if (reactions.has(key)) reactions.delete(key);
    else reactions.set(key, true);
  }, origin);
}

/** Sets (on) or clears (off) this actor's reaction; repeating a call changes nothing (agents use this, not the toggle). */
export function setReaction(doc: Y.Doc, actor: Actor, cardId: string, emoji: string, on: boolean, origin: unknown = LOCAL) {
  doc.transact(() => {
    const reactions = cardsMap(doc).get(cardId)?.get('reactions') as Y.Map<true> | undefined;
    if (!reactions || emoji.includes('|') || emoji.length > 16) return;
    // See toggleReaction above: keyed by reactorId, not userId, so agents don't toggle their owner's reaction.
    const key = `${emoji}|${reactorId(actor)}`;
    if (on && !reactions.has(key)) reactions.set(key, true);
    if (!on && reactions.has(key)) reactions.delete(key);
  }, origin);
}

export function moveSpark(doc: Y.Doc, cardId: string, index: number, origin: unknown = LOCAL) {
  doc.transact(() => {
    if (!cardsMap(doc).has(cardId)) return;
    const spark = sparkArray(doc);
    removeId(spark, cardId);
    spark.insert(Math.max(0, Math.min(index, spark.length)), [cardId]);
  }, origin);
}

function todoItems(doc: Y.Doc, cardId: string) {
  return cardsMap(doc).get(cardId)?.get('items') as Y.Array<Y.Map<any>> | undefined;
}

export function addTodoItem(doc: Y.Doc, actor: Actor, cardId: string, text: string, origin: unknown = LOCAL): string {
  const item = todoItem(text.slice(0, 500));
  doc.transact(() => {
    todoItems(doc, cardId)?.push([item]);
    const card = cardsMap(doc).get(cardId);
    if (card) stamp(card, actor);
  }, origin);
  return item.get('id');
}

export function setTodoItem(doc: Y.Doc, actor: Actor, cardId: string, itemId: string, fields: { text?: string; done?: boolean }, origin: unknown = LOCAL) {
  doc.transact(() => {
    const item = todoItems(doc, cardId)?.toArray().find((m) => m.get('id') === itemId);
    if (!item) return;
    if (fields.text !== undefined) item.set('text', fields.text.slice(0, 500));
    if (fields.done !== undefined) item.set('done', fields.done);
    const card = cardsMap(doc).get(cardId);
    if (card) stamp(card, actor);
  }, origin);
}

export function removeTodoItem(doc: Y.Doc, cardId: string, itemId: string, origin: unknown = LOCAL) {
  doc.transact(() => {
    const items = todoItems(doc, cardId);
    if (!items) return;
    for (let i = items.length - 1; i >= 0; i--) if (items.get(i).get('id') === itemId) items.delete(i, 1);
  }, origin);
}

/** Board "Delete": removes the card from this board, or deletes it when this is its only board. */
export function removeOrDeleteCard(doc: Y.Doc, actor: Actor, boardId: string, cardId: string, origin: unknown = LOCAL) {
  if (cardBoards(doc, cardId).length > 1) removeFromBoard(doc, boardId, cardId, origin);
  else deleteCard(doc, actor, cardId, origin);
}

/**
 * Reading order for a board (the phone's list view and the agent's get_board): top to bottom, then
 * left to right; a column's cards follow their column. Malformed placements and columns are skipped;
 * a column without a valid position reads last; a card whose column is missing is listed as a free
 * card, the way the canvas draws it.
 */
export function boardReadingOrder(doc: Y.Doc, boardId: string): string[] {
  const board = boardMap(doc, boardId);
  if (!board) return [];
  const LAST = Number.MAX_SAFE_INTEGER;
  const items: { y: number; x: number; ids: string[] }[] = [];
  const listed = new Set<string>();
  const cols = board.get('columns');
  if (cols instanceof Y.Map) {
    cols.forEach((col: unknown, colId: string) => {
      if (!(col instanceof Y.Map)) return;
      const pos = posSchema.safeParse(col.get('pos'));
      const ids = columnCards(doc, boardId, colId);
      ids.forEach((id) => listed.add(id));
      items.push({ y: pos.success ? pos.data.y : LAST, x: pos.success ? pos.data.x : LAST, ids });
    });
  }
  const placements = board.get('placements');
  if (placements instanceof Y.Map) {
    placements.forEach((p: unknown, id: string) => {
      if (!(p instanceof Y.Map) || listed.has(id)) return;
      const pl = parsePlacement(p.toJSON());
      if (pl) items.push({ y: pl.pos.y, x: pl.pos.x, ids: [id] });
    });
  }
  return items.sort((a, b) => a.y - b.y || a.x - b.x).flatMap((i) => i.ids);
}

/** Left of everything on the board, top-aligned with it; the origin on an empty board. Malformed entries are skipped. */
function columnSpot(board: Y.Map<any>): Pos {
  let minX = Infinity;
  let minY = Infinity;
  const consider = (v: unknown) => {
    const p = posSchema.safeParse(v);
    if (p.success) {
      minX = Math.min(minX, p.data.x);
      minY = Math.min(minY, p.data.y);
    }
  };
  // The board's own placements/columns fields might not be Y.Maps at all (malformed data): then
  // there's nothing to consider, same as an empty board, rather than a crash.
  const placements = board.get('placements');
  if (placements instanceof Y.Map) placements.forEach((p) => p instanceof Y.Map && consider(p.get('pos')));
  const cols = board.get('columns');
  if (cols instanceof Y.Map) cols.forEach((c) => c instanceof Y.Map && consider(c.get('pos')));
  return Number.isFinite(minX) ? { x: Math.round(minX - 320), y: Math.round(minY) } : { x: 0, y: 0 };
}

/** Creates the fixed-ID column if missing (inside the caller's transaction) and returns its position. */
function ensureColumnIn(board: Y.Map<any>, columnId: string, title: string): Pos {
  const cols = board.get('columns') as Y.Map<any>;
  const existing = cols.get(columnId);
  if (existing !== undefined) {
    const pos = existing instanceof Y.Map ? posSchema.safeParse(existing.get('pos')) : null;
    return pos?.success ? pos.data : { x: 0, y: 0 };
  }
  const pos = columnSpot(board);
  const col = cols.set(columnId, new Y.Map<any>());
  col.set('title', title.slice(0, 200));
  col.set('pos', pos);
  col.set('w', 280);
  col.set('cardIds', new Y.Array<string>());
  return pos;
}

/**
 * Makes sure the board has the fixed column (Inbox, 🤖 Agent) and returns its position. A creator: an
 * unknown board throws. Two people creating it at once converge on one column (same key); a card whose
 * entry lost that race is drawn free at its own position, never lost (the column rule: placement wins).
 */
export function ensureColumn(doc: Y.Doc, boardId: string, columnId: string, title: string, origin: unknown = LOCAL): Pos {
  let pos: Pos = { x: 0, y: 0 };
  doc.transact(() => {
    const board = boardMap(doc, boardId);
    if (!board) throw new OpsError('unknown_board', `Unknown board ${boardId}`);
    pos = ensureColumnIn(board, columnId, title);
  }, origin);
  return pos;
}

function findingMap(doc: Y.Doc, cardId: string): Y.Map<any> | undefined {
  const card = cardsMap(doc).get(cardId);
  return card instanceof Y.Map && card.get('kind') === 'finding' ? card : undefined;
}

/**
 * A person accepts a suggested finding. Dropped on a board (`at`), it goes where it was dropped;
 * otherwise into the Research board's Inbox column. Agents never call this (the server refuses them).
 */
export function acceptFinding(doc: Y.Doc, actor: Actor, cardId: string, at?: { boardId: string; pos: Pos }, origin: unknown = LOCAL) {
  doc.transact(() => {
    const card = findingMap(doc, cardId);
    const board = boardMap(doc, at?.boardId ?? SHARED_BOARDS.research);
    if (!card || !board) return;
    card.set('trayState', 'accepted');
    card.set('trayBy', actor);
    card.set('trayAt', nowIso());
    stamp(card, actor);
    if (at) {
      setPlacement(board, cardId, 'finding', at.pos, null);
      return;
    }
    const pos = ensureColumnIn(board, FIXED_COLUMNS.inbox, 'Inbox');
    setPlacement(board, cardId, 'finding', pos, FIXED_COLUMNS.inbox);
    // A malformed Inbox value (not a usable column): keep the card free rather than pointing at it.
    if (!insertInColumn(board, cardId, FIXED_COLUMNS.inbox, Infinity)) (board.get('placements') as Y.Map<Y.Map<any>>).get(cardId)?.set('columnId', null);
  }, origin);
}

function setTrayState(doc: Y.Doc, actor: Actor, cardId: string, state: 'dismissed' | 'suggested', origin: unknown) {
  doc.transact(() => {
    const card = findingMap(doc, cardId);
    if (!card) return;
    // A stale dismiss arriving after the finding was already accepted onto a board must not
    // overwrite the accepter's trayBy/trayAt; a restore only makes sense for a finding that's
    // actually dismissed (restoring a suggested or accepted finding would change nothing anyway,
    // but would still stamp trayBy/trayAt and the card's updatedBy).
    if (state === 'dismissed' && cardBoards(doc, cardId).length > 0) return;
    if (state === 'suggested' && effectiveTrayState(doc, cardId) !== 'dismissed') return;
    card.set('trayState', state);
    card.set('trayBy', state === 'suggested' ? null : actor);
    card.set('trayAt', state === 'suggested' ? null : nowIso());
    stamp(card, actor);
  }, origin);
}

/** A person dismisses a suggested finding: it leaves the tray (and is listed under "Dismissed"). */
export function dismissFinding(doc: Y.Doc, actor: Actor, cardId: string, origin: unknown = LOCAL) {
  setTrayState(doc, actor, cardId, 'dismissed', origin);
}

/** A person puts a dismissed finding back into the tray. */
export function restoreFinding(doc: Y.Doc, actor: Actor, cardId: string, origin: unknown = LOCAL) {
  setTrayState(doc, actor, cardId, 'suggested', origin);
}

/**
 * A finding's tray state as everyone should see it. Being on a board means accepted, whatever
 * `trayState` says: when one person accepts while another dismisses, both replicas converge on
 * "accepted" (the finding stays where it was put) instead of a "dismissed" card sitting on the board.
 */
export function effectiveTrayState(doc: Y.Doc, cardId: string): TrayState | null {
  const card = findingMap(doc, cardId);
  if (!card) return null;
  if (cardBoards(doc, cardId).length > 0) return 'accepted';
  const state = card.get('trayState');
  return state === 'accepted' || state === 'dismissed' ? state : 'suggested';
}

/** Findings by effective tray state, newest first. */
export function trayFindings(doc: Y.Doc): Record<TrayState, string[]> {
  const found: Record<TrayState, { id: string; at: string }[]> = { suggested: [], accepted: [], dismissed: [] };
  cardsMap(doc).forEach((card, id) => {
    if (!(card instanceof Y.Map) || card.get('kind') !== 'finding') return;
    found[effectiveTrayState(doc, id)!].push({ id, at: String(card.get('createdAt') ?? '') });
  });
  const newestFirst = (xs: { id: string; at: string }[]) => xs.sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id)).map((x) => x.id);
  return { suggested: newestFirst(found.suggested), accepted: newestFirst(found.accepted), dismissed: newestFirst(found.dismissed) };
}

// --- Routes (spec §9) ----------------------------------------------------------------------------

export interface NewRoute {
  /** Given by the server for GPX imports (its route_geometry row is written first); random otherwise. */
  id?: string;
  name: string;
  source: RouteSource;
  colour: string;
  waypoints?: LonLat[] | null;
  displayLine: LonLat[][];
  lengthM: number;
  geometryHash: string;
  assetId?: string | null;
}

const cloneLonLat = (p: LonLat): LonLat => [p[0], p[1]];
const cloneLine = (line: LonLat[][]): LonLat[][] => line.map((part) => part.map(cloneLonLat));

export function addRoute(doc: Y.Doc, actor: Actor, input: NewRoute, origin: unknown = LOCAL): string {
  const id = input.id ?? randomId('rt');
  doc.transact(() => {
    // An id collision (a GPX import racing another write under the same id) must not silently
    // overwrite the existing route; check before writing anything, like every other creator here.
    if (routesMap(doc).has(id)) throw new OpsError('invalid_input', `Route ${id} already exists`);
    const at = nowIso();
    const r = routesMap(doc).set(id, new Y.Map<any>());
    r.set('id', id);
    r.set('name', input.name.slice(0, LIMITS.title));
    r.set('source', input.source);
    r.set('colour', input.colour.toLowerCase());
    // Copied, not the caller's own arrays: waypoints and displayLine[0] must never be the same JS
    // array object, and nothing the caller does to its arrays afterwards should reach the document.
    r.set('waypoints', input.waypoints ? input.waypoints.map(cloneLonLat) : null);
    r.set('displayLine', cloneLine(input.displayLine));
    r.set('lengthM', Math.round(input.lengthM));
    r.set('geometryHash', input.geometryHash);
    r.set('assetId', input.assetId ?? null);
    r.set('createdBy', actor);
    r.set('createdAt', at);
    r.set('updatedBy', actor);
    r.set('updatedAt', at);
  }, origin);
  return id;
}

/** A route drawn by clicking points: straight segments between them. Null when there aren't two different (valid) points. */
export function addDrawnRoute(doc: Y.Doc, actor: Actor, name: string, colour: string, waypoints: LonLat[], origin: unknown = LOCAL): string | null {
  const f = drawnRouteFields(waypoints);
  if (!f) return null;
  return addRoute(doc, actor, { name, colour, source: 'drawn', ...f }, origin);
}

/** Renames or recolours a route; anything else about it is fixed once made. A malformed colour is ignored. */
export function updateRoute(doc: Y.Doc, actor: Actor, routeId: string, fields: { name?: string; colour?: string }, origin: unknown = LOCAL) {
  doc.transact(() => {
    const r = routesMap(doc).get(routeId);
    if (!(r instanceof Y.Map)) return;
    let changed = false;
    if (fields.name !== undefined) {
      r.set('name', fields.name.slice(0, LIMITS.title));
      changed = true;
    }
    if (fields.colour !== undefined && /^#[0-9a-f]{6}$/i.test(fields.colour)) {
      r.set('colour', fields.colour.toLowerCase());
      changed = true;
    }
    if (changed) {
      r.set('updatedBy', actor);
      r.set('updatedAt', nowIso());
    }
  }, origin);
}

/**
 * Removes a route from the document. Its full-resolution geometry (route_geometry) and its original
 * GPX file stay in SQLite on purpose: an undo or a snapshot restore brings the route back with the
 * same geometryHash and finds them again.
 */
export function deleteRoute(doc: Y.Doc, routeId: string, origin: unknown = LOCAL) {
  doc.transact(() => {
    routesMap(doc).delete(routeId);
  }, origin);
}
